import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader, Image, AlertCircle, Download, RefreshCw, CheckCircle, PlusCircle } from 'lucide-react';
import API_URL from '../config';
import logger from '../utils/logger';


const VisionBoard = ({ onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [userData, setUserData] = useState({
    mission: '',
    vision: '',
    oneWordFocus: '',
    affirmations: [],
    goals: []
  });
  
  const [goalInput, setGoalInput] = useState('');
  const [generatedImages, setGeneratedImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [boardLayout, setBoardLayout] = useState('grid'); // 'grid', 'pinterest', 'collage'
  const [finalBoard, setFinalBoard] = useState(null);
  const [customGoals, setCustomGoals] = useState([]);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Initialize with empty data
      setUserData({
        mission: '',
        vision: '',
        oneWordFocus: '',
        affirmations: [],
        goals: []
      });
      
      setLoading(false);
    } catch (error) {
      logger.error('Error fetching user data:', error);
      setError('Failed to load your data. Please try again.');
      setLoading(false);
    }
  };

  const addCustomGoal = () => {
    if (goalInput.trim() === '') {
      setError('Please enter a goal');
      return;
    }
    
    setCustomGoals([...customGoals, goalInput]);
    setGoalInput('');
    setMessage('Goal added successfully!');
    
    // Clear message after 2 seconds
    setTimeout(() => {
      setMessage('');
    }, 2000);
  };

  const generateImageForGoal = async (goal) => {
    setGenerating(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      
      // Use exactly what the user typed as the prompt
      const response = await axios.post(
        `${API_URL}/ai/generate-image`,
        { prompt: goal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newImage = {
        imageUrl: response.data.imageUrl,
        prompt: goal,
        type: 'goal'
      };
      
      setGeneratedImages(prev => [...prev, newImage]);
      setSelectedImages(prev => [...prev, response.data.imageUrl]); // Auto-select generated image
      setMessage('Image generated successfully!');
    } catch (error) {
      logger.error('Error generating image for goal:', error);
      setError('Failed to generate image. Please try again later.');
    } finally {
      setGenerating(false);
    }
  };

  const generateImages = async () => {
    setGenerating(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      
      // Prepare prompts from user data
      const prompts = [];
      
      // Add custom goals
      customGoals.forEach(goal => {
        if (goal && goal.trim()) {
          prompts.push({
            text: goal,
            type: 'goal'
          });
        }
      });
      
      // Check if we have any prompts to generate images for
      if (prompts.length === 0) {
        throw new Error('No valid prompts found. Please add goals or affirmations.');
      }
      
      // Generate images for each prompt
      const generatedResults = await Promise.all(
        prompts.map(async (prompt) => {
          try {
            const response = await axios.post(
              `${API_URL}/ai/generate-image`,
              { prompt: prompt.text },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            return {
              imageUrl: response.data.imageUrl,
              prompt: prompt.text,
              type: prompt.type
            };
          } catch (err) {
            logger.error(`Error generating image for prompt: ${prompt.text}`, err);
            return null;
          }
        })
      );
      
      // Filter out null results
      const validResults = generatedResults.filter(result => result !== null);
      
      if (validResults.length === 0) {
        throw new Error('Could not generate any images. Please try again.');
      }
      
      setGeneratedImages(validResults);
      setSelectedImages(validResults.map(img => img.imageUrl)); // Auto-select all images
      setMessage('Images generated successfully!');
    } catch (error) {
      logger.error('Error generating images:', error);
      setError('Failed to generate images: ' + (error.message || 'Please try again later.'));
    } finally {
      setGenerating(false);
    }
  };

  const userDataTextContent = (htmlContent) => {
    // Simple function to extract text from HTML content
    if (!htmlContent) return '';
    
    // Check if htmlContent is a string
    if (typeof htmlContent !== 'string') {
      return '';
    }
    
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      return tempDiv.textContent || tempDiv.innerText || '';
    } catch (e) {
      logger.error('Error extracting text from HTML:', e);
      return '';
    }
  };

  const enhancePrompt = (text, type) => {
    // Just return the text without any enhancements
    return text;
  };

  const toggleImageSelection = (imageUrl) => {
    if (selectedImages.includes(imageUrl)) {
      setSelectedImages(selectedImages.filter(url => url !== imageUrl));
    } else {
      setSelectedImages([...selectedImages, imageUrl]);
    }
  };

  const createVisionBoard = async () => {
    if (!selectedImages || selectedImages.length === 0) {
      setError('Please select at least one image for your vision board.');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Call backend to create the vision board
      const response = await axios.post(
        `${API_URL}/ai/create-vision-board`,
        {
          images: selectedImages,
          layout: boardLayout,
          text: '',
          affirmations: []
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!response.data || !response.data.boardUrl) {
        throw new Error('Invalid response from server. Board URL not received.');
      }
      
      setFinalBoard(response.data.boardUrl);
      setMessage('Vision board created successfully!');
    } catch (error) {
      logger.error('Error creating vision board:', error);
      setError('Failed to create vision board: ' + (error.message || 'Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const saveVisionBoard = async () => {
    if (!finalBoard) {
      setError('No vision board to save.');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API_URL}/ai/save-vision-board`,
        { imageUrl: finalBoard },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessage('Vision board saved to your profile!');
      
      // If onComplete callback is provided, call it
      if (typeof onComplete === 'function') {
        setTimeout(() => {
          onComplete();
        }, 2000);
      }
    } catch (error) {
      logger.error('Error saving vision board:', error);
      setError('Failed to save vision board: ' + (error.message || 'Please try again.'));
    }
  };

  const downloadVisionBoard = () => {
    if (!finalBoard) {
      setError('No vision board to download.');
      return;
    }
    
    try {
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = finalBoard;
      link.download = 'my-vision-board.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setMessage('Vision board download started!');
    } catch (error) {
      logger.error('Error downloading vision board:', error);
      setError('Failed to download vision board: ' + (error.message || 'Please try again.'));
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <h1 className="text-3xl font-bold mb-6">AI Vision Board Creator</h1>
      
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative flex items-center">
          <AlertCircle className="mr-2" size={20} />
          <span>{error}</span>
        </div>
      )}
      
      {message && (
        <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative flex items-center">
          <CheckCircle className="mr-2" size={20} />
          <span>{message}</span>
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">Create a Visual Goal</h2>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Enter your goal:
          </label>
          <div className="flex items-center">
            <input
              type="text"
              value={goalInput}
              onChange={(e) => setGoalInput(e.target.value)}
              placeholder="e.g., Run a marathon, Learn to play guitar, Start my own business"
              className="flex-grow p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 mr-2"
            />
            <button
              onClick={addCustomGoal}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded focus:outline-none focus:shadow-outline flex-shrink-0"
            >
              <PlusCircle size={20} />
            </button>
          </div>
        </div>
        
        {/* Custom Goals Section */}
        {customGoals.length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold mb-2">Your Goals:</h3>
            <div className="space-y-2">
              {customGoals.map((goal, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center bg-gray-50 p-3 rounded-lg"
                >
                  <span>{goal}</span>
                  <button
                    onClick={() => generateImageForGoal(goal)}
                    disabled={generating}
                    className={`bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center ${
                      generating ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {generating ? (
                      <Loader className="animate-spin mr-2" size={16} />
                    ) : (
                      <RefreshCw className="mr-2" size={16} />
                    )}
                    Visualize
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="pt-4">
          <button
            onClick={generateImages}
            disabled={generating}
            className={`w-full bg-blue-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center justify-center ${
              generating ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
            }`}
          >
            {generating ? (
              <>
                <Loader className="animate-spin mr-2" size={20} />
                Generating Images...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2" size={20} />
                Generate All Vision Board Images
              </>
            )}
          </button>
        </div>
      </div>
      
      {generatedImages.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Select Images for Your Vision Board</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            {generatedImages.map((image, index) => (
              <div 
                key={index} 
                className={`relative border-2 rounded-lg overflow-hidden cursor-pointer ${
                  selectedImages.includes(image.imageUrl) ? 'border-blue-500' : 'border-gray-200'
                }`}
                onClick={() => toggleImageSelection(image.imageUrl)}
              >
                <img 
                  src={image.imageUrl} 
                  alt={`Vision image for: ${image.prompt}`}
                  className="w-full h-40 object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-sm">
                  {image.prompt && image.prompt.length > 30 ? `${image.prompt.substring(0, 30)}...` : (image.prompt || 'Vision Image')}
                </div>
                {selectedImages.includes(image.imageUrl) && (
                  <div className="absolute top-2 right-2 bg-blue-500 rounded-full p-1">
                    <CheckCircle className="text-white" size={16} />
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="mb-6">
            <h3 className="font-semibold mb-2">Select Board Layout:</h3>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="layout"
                  value="grid"
                  checked={boardLayout === 'grid'}
                  onChange={() => setBoardLayout('grid')}
                  className="mr-2"
                />
                Grid
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="layout"
                  value="pinterest"
                  checked={boardLayout === 'pinterest'}
                  onChange={() => setBoardLayout('pinterest')}
                  className="mr-2"
                />
                Pinterest
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="layout"
                  value="collage"
                  checked={boardLayout === 'collage'}
                  onChange={() => setBoardLayout('collage')}
                  className="mr-2"
                />
                Collage
              </label>
            </div>
          </div>
          
          <button
            onClick={createVisionBoard}
            disabled={loading || !selectedImages || selectedImages.length === 0}
            className={`w-full bg-green-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
              loading || !selectedImages || selectedImages.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700'
            }`}
          >
            {loading ? 'Creating Vision Board...' : 'Create Vision Board'}
          </button>
        </div>
      )}
      
      {finalBoard && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Your Vision Board</h2>
          <div className="mb-4">
            <img 
              src={finalBoard} 
              alt="Your Vision Board" 
              className="w-full rounded-lg shadow-md"
            />
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <button
              onClick={saveVisionBoard}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center justify-center"
            >
              <CheckCircle className="mr-2" size={20} />
              Save to My Profile
            </button>
            <button
              onClick={downloadVisionBoard}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center justify-center"
            >
              <Download className="mr-2" size={20} />
              Download Vision Board
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisionBoard;